import { skipToken } from "@reduxjs/toolkit/query";
import { buildOutcomeGreenRateLabel } from "@repo/app-types/entities/outcome";
import {
  checkPlaybookHasKnowledgeBaseAdherence,
  getOutcomeDefinitionFromPlaybook,
} from "@repo/app-types/entities/playbook";
import { asTeammateId } from "@repo/app-types/entities/teammate";
import { isStaticTeammateAgentIdentity } from "@repo/app-types/entities/teammateAgent";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { PageHeaderFilterBarPortal } from "@repo/ui/Navigation/pageHeaderFilterBarSlot";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { PillSkeleton } from "@repo/ui/Pills/pillSkeleton";
import { skeletonStyle } from "@repo/ui/Skeletons/skeletons";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { IconExternalLink } from "@tabler/icons-react";
import { type FC, useCallback, useMemo } from "react";
import { Heading } from "react-aria-components";
import { useNavigate, useParams } from "react-router";
import { twMerge } from "tailwind-merge";
import {
  usePlaybookTeammatePerformanceQuery,
  usePlaybookTeammatePlaybookCriteriaScoresQuery,
} from "../../api/entities/performancePlaybook.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useAllPlaybookTestSpecsQuery } from "../../api/entities/playbookTestSpec.js";
import {
  useGetTeammateAgentQuery,
  useGetTeammateAgentTestSpecsResultsQuery,
} from "../../api/entities/teammateAgent.js";
import { appPaths } from "../../app/appPaths.js";
import { PageBody } from "../../components/layout/pageLayout.js";
import { calculateTestPassRate } from "../../components/recordingAssessmentEditor/feedbackAccuracyHelpers.js";
import { NavigationSlideoverContainer } from "../../hooks/useModalNavigation.js";
import { buildPlaybookScope } from "../filters/buildFilterQueryArgs.js";
import { useFilters } from "../filters/filters.js";
import { GradingPassRatePill } from "../teammatePerformance/gradingPassRatePill.js";
import { KnowledgeBaseAdherenceStatsCard } from "../teammatePerformance/knowledgeBaseAdherenceStatsCard.js";
import { OutcomeRatePill } from "../teammatePerformance/outcomeRatePill.js";
import { buildOutcomeMultiLineData } from "../teammatePerformance/outcomeTrendChartData.js";
import { TeammateCallCount } from "../teammatePerformance/teammateCallCount.js";
import { TrainingTeammatePagePlaybook } from "../teammatePerformance/trainingTeammatePagePlaybook.js";
import {
  countTrendDays,
  TrendChart,
} from "../teammatePerformance/trendChart.js";
import { ToolUsageAccuracyOverviewCardContainer } from "./toolUsageAccuracyOverviewCardContainer.js";

export const TeammateAgentOverviewTab: FC = () => {
  const { teammateAgentId: teammateAgentIdParam } = useParams();
  const navigate = useNavigate();
  if (!teammateAgentIdParam) {
    throw new Error("Missing params.");
  }
  const teammateAgentId = asTeammateId(teammateAgentIdParam);

  const now = useNow();

  const { data: teammateAgent } = useGetTeammateAgentQuery(teammateAgentId);
  const teammateAgentConfig = teammateAgent?.teammate_agent_config;
  const playbookId = teammateAgentConfig?.playbook_id;
  const teammateId = teammateAgent?.id ?? teammateAgentId;
  // Static agents skip criteria/section grading, so they have no playbook
  // scores to show.
  const isStaticAgent =
    teammateAgentConfig !== undefined &&
    isStaticTeammateAgentIdentity(teammateAgentConfig.identity);

  const [{ dateRange, dateBasis }, renderFilters] = useFilters(
    ["dateRange", "dateBasis"],
    { playbookId, teammateId },
  );
  const trendDays = countTrendDays(dateRange);

  const queryArgs =
    playbookId && teammateId
      ? {
          playbookId,
          teammateId,
          scope: buildPlaybookScope(now, playbookId, dateRange, dateBasis),
          filters: [],
        }
      : skipToken;

  const {
    data: teammatePerformanceData,
    isFetching: isTeammatePerformanceLoading,
    isError: isTeammatePerformanceError,
  } = usePlaybookTeammatePerformanceQuery(queryArgs);
  // On error, RTK Query's `data` keeps the last successful (possibly
  // differently-filtered) result — drop it so we render empty states instead
  // of stale numbers.
  const teammatePerformance = isTeammatePerformanceError
    ? undefined
    : teammatePerformanceData;

  // Fetch playbook for assessment tree (needed for accuracy calculation)
  const { data: playbook, isLoading: isLoadingPlaybook } = usePlaybookByIdQuery(
    playbookId ?? skipToken,
  );

  // Fetch playbook criteria scores for outcome / KB / tool-usage cards
  const {
    data: playbookCriteriaScoresDataRaw,
    isLoading: isLoadingPlaybookCriteriaScores,
    isError: isPlaybookCriteriaScoresError,
  } = usePlaybookTeammatePlaybookCriteriaScoresQuery(queryArgs);
  const playbookCriteriaScoresData = isPlaybookCriteriaScoresError
    ? undefined
    : playbookCriteriaScoresDataRaw;
  const outcomeGreenRate = playbookCriteriaScoresData?.outcomeGreenRate;
  const outcomeValueRateSparklines =
    playbookCriteriaScoresData?.outcomeValueRateSparklines;
  const playbookKnowledgeBaseAdherenceScore =
    playbookCriteriaScoresData?.knowledgeBaseScore;
  const playbookToolUsageAccuracyScores =
    playbookCriteriaScoresData?.toolUsageAccuracyScores;

  // Get outcome definition from playbook
  const outcomeDefinition = getOutcomeDefinitionFromPlaybook(playbook);

  // Build multi-line data for the outcome trend chart
  const outcomeMultiLineData = useMemo(() => {
    if (!outcomeDefinition) {
      return [];
    }
    return buildOutcomeMultiLineData(
      outcomeValueRateSparklines,
      outcomeDefinition,
    );
  }, [outcomeValueRateSparklines, outcomeDefinition]);

  // Fetch all test specs and results for the playbook
  const { data: testSpecsResults, isFetching: isTestSpecsResultsLoading } =
    useGetTeammateAgentTestSpecsResultsQuery(
      playbookId ? { teammateAgentId, playbookId } : skipToken,
    );

  const { data: allTestSpecs, isFetching: isTestSpecsLoading } =
    useAllPlaybookTestSpecsQuery(playbookId ? { playbookId } : skipToken);

  // Calculate test case pass rate
  const testCasePassRate = useMemo(() => {
    if (!testSpecsResults || !playbook?.assessment_tree || !allTestSpecs) {
      return null;
    }

    return calculateTestPassRate(
      testSpecsResults,
      playbook.assessment_tree,
      allTestSpecs.length,
    );
  }, [testSpecsResults, playbook?.assessment_tree, allTestSpecs]);

  const renderCallCount = () => {
    if (isTeammatePerformanceLoading) {
      return <PillSkeleton size={Size.SMALL} />;
    }

    if (teammatePerformance) {
      return (
        <span className="text-base font-medium text-foreground">
          <TeammateCallCount row={teammatePerformance} />
        </span>
      );
    }

    return <span className="text-base text-muted-foreground">--</span>;
  };

  const callVolumeData = teammatePerformance?.recording_count_sparkline;

  const renderCallVolumeTrend = () => {
    if (isTeammatePerformanceLoading) {
      return <div className={skeletonStyle({ className: "h-8 w-full" })} />;
    }

    if (!callVolumeData) {
      return (
        <span className="text-sm text-muted-foreground">No activity yet</span>
      );
    }

    return (
      <TrendChart
        className="h-8"
        lines={[{ key: "callVolume", data: callVolumeData }]}
        minYRange={5}
        renderValue={(value) => value.toString()}
        showsXAxisLabels={false}
        showsYAxisLabels={true}
        getColorClassName={() => "text-muted-foreground"}
      />
    );
  };

  // Test content will be rendered as a third column in the first row if there is no outcome definition,
  // otherwise it will be rendered as a second column in the second row if there is one.
  const renderTestsContent = () => (
    <div>
      <p className="text-xs font-semibold text-foreground">Tests</p>
      <div className="mt-2 flex items-center gap-3">
        <GradingPassRatePill
          size={Size.SMALL}
          passRate={
            testCasePassRate ? testCasePassRate.percentage / 100 : undefined
          }
          isLoading={isTestSpecsLoading || isTestSpecsResultsLoading}
        />
        <Button
          variant={ButtonVariant.Tertiary}
          onClick={() => {
            navigate(appPaths.teammateAgentTesting.build({ teammateAgentId }));
          }}
          size={Size.SMALL}
          icon={<IconExternalLink className="h-4 w-4" />}
        >
          View tests
        </Button>
      </div>
    </div>
  );

  // Custom path function for criterion navigation in the agent context
  const getCriterionPath = useCallback(
    (criterionId: string) =>
      `teammates/${encodeURIComponent(teammateId)}/playbooks/${encodeURIComponent(playbookId ?? "")}/criteria/${encodeURIComponent(criterionId)}`,
    [teammateId, playbookId],
  );

  return (
    <>
      <PageHeaderFilterBarPortal>
        {renderFilters({
          isLoading: isTeammatePerformanceLoading,
        })}
      </PageHeaderFilterBarPortal>

      <PageBody width={PageWidth.Wide}>
        {/* Performance Stats */}
        <div className="rounded-lg border border-border-light bg-white">
          <div
            className={twMerge(
              "grid gap-6 p-4",
              outcomeDefinition ? "grid-cols-2" : "grid-cols-3",
            )}
          >
            <div>
              <p className="text-xs font-semibold text-foreground">
                {teammatePerformance?.recording_count_sparkline === undefined
                  ? "Activity (30d)"
                  : `Activity (${teammatePerformance.recording_count_sparkline.length}d)`}
              </p>
              <div className="mt-3">{renderCallVolumeTrend()}</div>
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground">
                Calls ({trendDays}d)
              </p>
              <div className="mt-2 flex items-center gap-3">
                {renderCallCount()}
                <Button
                  variant={ButtonVariant.Tertiary}
                  onClick={() => {
                    navigate(
                      appPaths.teammateAgentCalls.build({ teammateAgentId }),
                    );
                  }}
                  size={Size.SMALL}
                  icon={<IconExternalLink className="h-4 w-4" />}
                >
                  View all calls
                </Button>
              </div>
            </div>
            {!outcomeDefinition ? renderTestsContent() : null}
          </div>

          {outcomeDefinition ? (
            <div className="border-t border-border-light p-4">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-x-2">
                    <p className="text-xs font-semibold text-foreground">
                      {buildOutcomeGreenRateLabel(outcomeDefinition)}
                    </p>
                    <OutcomeRatePill
                      size={Size.SMALL}
                      outcomeRate={outcomeGreenRate}
                      isLoading={isLoadingPlaybookCriteriaScores}
                    />
                  </div>
                  {outcomeMultiLineData.length > 0 ? (
                    <div className="mt-3">
                      <TrendChart
                        className="h-20 w-full"
                        minYRange={0.1}
                        showsXAxisLabels
                        showsYAxisLabels
                        showsLegend
                        lines={outcomeMultiLineData}
                        renderValue={(value) => `${Math.round(value * 100)}%`}
                      />
                    </div>
                  ) : (
                    <div className="mt-3">
                      <span className="text-sm text-muted-foreground">
                        No trend data
                      </span>
                    </div>
                  )}
                </div>
                {renderTestsContent()}
              </div>
            </div>
          ) : null}
        </div>

        {playbook !== undefined &&
        checkPlaybookHasKnowledgeBaseAdherence(playbook) ? (
          <section className="space-y-4">
            <Heading
              level={2}
              className="text-base font-semibold text-foreground"
            >
              Knowledge base adherence
            </Heading>

            <KnowledgeBaseAdherenceStatsCard
              data={playbookKnowledgeBaseAdherenceScore}
              isLoading={isLoadingPlaybookCriteriaScores}
            />
          </section>
        ) : null}

        {teammateAgentConfig !== undefined &&
        teammateAgentConfig.tool_ids.length > 0 ? (
          <section className="space-y-4">
            <Heading
              level={2}
              className="text-base font-semibold text-foreground"
            >
              Tool usage accuracy
            </Heading>

            <ToolUsageAccuracyOverviewCardContainer
              playbookId={playbookId}
              teammateId={teammateId}
              toolIds={teammateAgentConfig.tool_ids}
              data={playbookToolUsageAccuracyScores}
              isLoading={isLoadingPlaybookCriteriaScores}
            />
          </section>
        ) : null}

        {!isStaticAgent && playbookId && (playbook || isLoadingPlaybook) ? (
          <div>
            <div className="mb-3 flex items-center gap-3">
              <Label size={Size.MEDIUM}>Playbook</Label>
              <GradingPassRatePill
                size={Size.SMALL}
                passRate={teammatePerformance?.scores.pass_rate}
                isLoading={isTeammatePerformanceLoading}
              />
            </div>
            <TrainingTeammatePagePlaybook
              playbookId={playbookId}
              teammateId={teammateId}
              getCriterionPath={getCriterionPath}
            />
          </div>
        ) : null}

        <NavigationSlideoverContainer />
      </PageBody>
    </>
  );
};
